import {
  FormControl,
  FormControlLabel,
  FormLabel,
  Radio,
  RadioGroup,
} from "@mui/material";
import { useInputAppearance } from "hooks/useInputAppearance";
import {
  FONT_PX,
  GAP_PX,
  InputGap,
  InputHeight,
  InputRadius,
  InputVariant,
  PAD_X_PX,
  PAD_Y_PX,
  RADIUS_PX,
} from "utils/inputAppearance";

/**
 * Definiowanie wyglądu pól tekstowych (współdzielony komponent Input) wraz z żywym
 * PODGLĄDEM (makieta). Zmiana zapisywana od razu (prywatna preferencja, AppSettings
 * input_appearance_v1) i stosowana globalnie: zaokrąglenie/wysokość/gęstość przez
 * zmienne CSS na polach .app-input, wariant przez prop MUI.
 */
const InputAppearanceTab = () => {
  const [view, setView] = useInputAppearance();

  const isOutline = view.variant === "outline";
  const isStandard = view.variant === "standard";
  const isFilled = view.variant === "filled";

  const radiusPx = RADIUS_PX[view.radius];
  const padY = PAD_Y_PX[view.height];
  const padX = PAD_X_PX[view.density];
  const fontPx = FONT_PX[view.density];

  // Wygląd pola wierny wariantom MUI TextField:
  //  outline  → pełna ramka, wszystkie narożniki zaokrąglone, białe tło
  //  filled   → BEZ pełnej ramki, tylko GÓRNE narożniki zaokrąglone, szare tło,
  //             dolna linia (underline); etykieta pływa WEWNĄTRZ u góry
  //  standard → tylko dolna linia, bez tła i zaokrągleń
  const fieldStyle: React.CSSProperties = isStandard
    ? {
        padding: `${padY} 0px`,
        fontSize: fontPx,
        border: "none",
        borderBottom: "1px solid rgba(0,0,0,0.42)",
        background: "transparent",
        color: "#48454f",
      }
    : isOutline
      ? {
          borderRadius: radiusPx,
          padding: `${padY} ${padX}`,
          fontSize: fontPx,
          border: "1px solid #8a7fb0",
          background: "#ffffff",
          color: "#48454f",
        }
      : {
          borderTopLeftRadius: radiusPx,
          borderTopRightRadius: radiusPx,
          borderBottomLeftRadius: 0,
          borderBottomRightRadius: 0,
          padding: `${padY} ${padX}`,
          fontSize: fontPx,
          border: "none",
          borderBottom: "1px solid rgba(0,0,0,0.42)",
          background: "rgba(0,0,0,0.06)",
          color: "#48454f",
        };

  const previewField = (label: string, value: string) => {
    const content = value || (
      <span style={{ color: "#a29ea3" }}>{`Wpisz ${label.toLowerCase()}…`}</span>
    );

    // Filled: etykieta pływa WEWNĄTRZ pola przy górnej krawędzi (bez notcha),
    // treść poniżej — dokładnie jak w domyślnym MUI filled.
    if (isFilled) {
      return (
        <div
          style={{
            ...fieldStyle,
            position: "relative",
            paddingTop: `calc(${padY} + 14px)`,
          }}
        >
          <span
            style={{
              position: "absolute",
              top: 6,
              left: padX,
              fontSize: 11,
              lineHeight: 1,
              color: "#79767a",
              pointerEvents: "none",
            }}
          >
            {label}
          </span>
          {content}
        </div>
      );
    }

    // Outline / Standard: etykieta na górnej krawędzi pola (notch tylko dla outline).
    return (
      <div style={{ position: "relative", marginTop: 6 }}>
        <div style={fieldStyle}>{content}</div>
        <span
          style={{
            position: "absolute",
            top: 0,
            left: isStandard ? 0 : 10,
            transform: "translateY(-50%)",
            padding: isStandard ? 0 : "0 4px",
            background: isStandard ? "transparent" : "#ffffff",
            fontSize: 12,
            lineHeight: 1,
            color: "#79767a",
            pointerEvents: "none",
          }}
        >
          {label}
        </span>
      </div>
    );
  };

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        gap: 24,
        padding: 8,
        maxWidth: 620,
      }}
    >
      {/* ── Podgląd wybranego wyglądu pól (nad parametrami) ── */}
      <div>
        <FormLabel sx={{ fontWeight: 600, color: "#1c1b1f" }}>Podgląd</FormLabel>
        <div
          style={{
            marginTop: 12,
            display: "flex",
            flexDirection: "column",
            gap: GAP_PX[view.gap],
            maxWidth: 360,
          }}
        >
          {previewField("Nazwa kontrahenta", "Kancelaria Krzak")}
          {previewField("Adres e-mail", "")}
        </div>
      </div>

      <FormControl>
        <FormLabel sx={{ fontWeight: 600, color: "#1c1b1f", mb: 0.5 }}>
          Styl pola
        </FormLabel>
        <RadioGroup
          row
          value={view.variant}
          onChange={(e) =>
            setView({ ...view, variant: e.target.value as InputVariant })
          }
        >
          <FormControlLabel
            value="standard"
            control={<Radio />}
            label="Standardowe"
          />
          <FormControlLabel
            value="outline"
            control={<Radio />}
            label="Konturowe"
          />
          <FormControlLabel
            value="filled"
            control={<Radio />}
            label="Wypełnione"
          />
        </RadioGroup>
      </FormControl>

      <FormControl>
        <FormLabel sx={{ fontWeight: 600, color: "#1c1b1f", mb: 0.5 }}>
          Zaokrąglenie narożników
        </FormLabel>
        <RadioGroup
          row
          value={view.radius}
          onChange={(e) =>
            setView({ ...view, radius: e.target.value as InputRadius })
          }
        >
          <FormControlLabel
            value="normal"
            control={<Radio />}
            label="Standardowe"
          />
          <FormControlLabel value="sharp" control={<Radio />} label="Ostre" />
          <FormControlLabel
            value="round"
            control={<Radio />}
            label="Zaokrąglone"
          />
        </RadioGroup>
      </FormControl>

      <FormControl>
        <FormLabel sx={{ fontWeight: 600, color: "#1c1b1f", mb: 0.5 }}>
          Wysokość pola
        </FormLabel>
        <RadioGroup
          row
          value={view.height}
          onChange={(e) =>
            setView({ ...view, height: e.target.value as InputHeight })
          }
        >
          <FormControlLabel
            value="standard"
            control={<Radio />}
            label="Standardowa"
          />
          <FormControlLabel value="low" control={<Radio />} label="Niska" />
          <FormControlLabel value="tall" control={<Radio />} label="Wysoka" />
        </RadioGroup>
      </FormControl>

      <FormControl>
        <FormLabel sx={{ fontWeight: 600, color: "#1c1b1f", mb: 0.5 }}>
          Odstęp (przerwa pomiędzy polami)
        </FormLabel>
        <RadioGroup
          row
          value={view.gap}
          onChange={(e) =>
            setView({ ...view, gap: e.target.value as InputGap })
          }
        >
          <FormControlLabel
            value="standard"
            control={<Radio />}
            label="Standardowy"
          />
          <FormControlLabel
            value="compact"
            control={<Radio />}
            label="Zwarty"
          />
          <FormControlLabel
            value="spacious"
            control={<Radio />}
            label="Luźny"
          />
        </RadioGroup>
      </FormControl>

      <p style={{ color: "#79767a", fontSize: 13, margin: 0, lineHeight: 1.5 }}>
        Ustawienia dotyczą pól tekstowych w całej aplikacji i są zapamiętywane dla
        Twojego konta. Zaokrąglenie, wysokość i odstęp działają od razu; zmiana
        stylu pola obejmuje pola przy kolejnym ich wyświetleniu.
      </p>
    </div>
  );
};

export default InputAppearanceTab;
